import { GoogleGenAI, Type } from '@google/genai';
import { Player, TeamInLeague, Result, GameState, DisplayFixture } from '../domain/entities';

// Singleton pattern to safely initialize AI client
let aiInstance: GoogleGenAI | null = null;
let keyChecked = false;

const getAiClient = (): GoogleGenAI | null => {
    if (keyChecked) {
        return aiInstance;
    }

    keyChecked = true; // Attempt initialization only once
    try {
        // Environment variable is injected at build time via Vite config
        // For Cloudflare Pages: Set GEMINI_API_KEY in build environment variables
        // For local development: Create .env file with VITE_GEMINI_API_KEY=your_key_here
        const apiKey = import.meta.env.VITE_GEMINI_API_KEY;

        if (!apiKey || apiKey === '') {
            console.warn("AI Service disabled: GEMINI_API_KEY environment variable not set.");
            console.warn("For local development, create a .env file with: VITE_GEMINI_API_KEY=your_key_here");
            console.warn("For Cloudflare Pages, set GEMINI_API_KEY in build environment variables.");
            aiInstance = null;
            return null;
        }
        aiInstance = new GoogleGenAI({ apiKey });
        console.log("AI Service initialized successfully with Gemini API");
        return aiInstance;
    } catch (e) {
        console.error("Failed to initialize GoogleGenAI, AI service will be disabled:", e);
        aiInstance = null;
        return null;
    }
};

const model = 'gemini-2.5-flash';

const getPlayerByIdUnsafe = (gameState: GameState, id: number): Player | undefined => {
    for (const league of Object.values(gameState.leagues)) {
        for (const team of league.teams) {
            const player = team.squad.find(p => p.id === id);
            if (player) return player;
        }
    }
    return undefined;
};

const aiPlayerResponseSchema = {
    type: Type.OBJECT,
    properties: {
      reasoning: { type: Type.STRING, description: 'Briefly explain your decisions for the week. If it is a management week, explain your transfer strategy as well.' },
      lineupChanges: {
        type: Type.ARRAY,
        description: 'Players to substitute in the starting XI to replace injured or out-of-form players.',
        items: {
          type: Type.OBJECT,
          properties: {
            playerInId: { type: Type.INTEGER, description: 'The ID of the player to bring into the lineup.' },
            playerOutId: { type: Type.INTEGER, description: 'The ID of the player to remove from the lineup.' },
          },
          required: ['playerInId', 'playerOutId'],
          propertyOrdering: ['playerInId', 'playerOutId']
        }
      },
      promotions: {
        type: Type.ARRAY,
        description: 'Only on a management week: Youth players to promote to the senior squad if there is space and they are good enough.',
        items: {
          type: Type.OBJECT,
          properties: {
            playerId: { type: Type.INTEGER, description: 'The ID of the youth player to promote.' }
          },
          required: ['playerId'],
          propertyOrdering: ['playerId']
        }
      },
      playersToSell: {
        type: Type.ARRAY,
        description: 'Only on a management week: Players to sell from the current squad to free up space or raise funds.',
        items: {
          type: Type.OBJECT,
          properties: {
            playerId: { type: Type.INTEGER, description: 'The ID of the player to sell.' }
          },
          required: ['playerId'],
          propertyOrdering: ['playerId']
        }
      },
      playersToBuy: {
        type: Type.ARRAY,
        description: 'Only on a management week: Transfer targets to purchase from the market to strengthen the team.',
        items: {
          type: Type.OBJECT,
          properties: {
            playerId: { type: Type.INTEGER, description: 'The ID of the player to attempt to buy.' }
          },
          required: ['playerId'],
          propertyOrdering: ['playerId']
        }
      }
    },
    required: ['reasoning', 'lineupChanges', 'promotions', 'playersToSell', 'playersToBuy'],
    propertyOrdering: ['reasoning', 'lineupChanges', 'promotions', 'playersToSell', 'playersToBuy']
};

const playerRoleAnalysisSchema = {
    type: Type.OBJECT,
    properties: {
        roleName: { type: Type.STRING, description: 'A specific football role name for the player (e.g., "Box-to-Box Midfielder", "Poacher").' },
        roleDescription: { type: Type.STRING, description: 'A 1-2 sentence description of this role, explaining how the player\'s key attributes make them suitable for it.' },
    },
    required: ['roleName', 'roleDescription'],
    propertyOrdering: ['roleName', 'roleDescription']
};


export const AiService = {
    generateAiPlayerActions: async (promptContext: any, managementInstruction: string): Promise<any> => {
        const ai = getAiClient();
        if (!ai) {
            throw new Error("AI Service is not available.");
        }
        
        const prompt = `You are an expert football manager AI. Your goal is to manage my team to success.
        Analyze the provided data and decide on a set of actions for the upcoming week.
        Your top priority is always to field the strongest possible team for the next match by making lineup changes.
        
        ${managementInstruction}

        Here is the current state of the club:
        ${JSON.stringify(promptContext, null, 2)}

        Based on this, provide a JSON object with your recommended actions.
        Follow the provided JSON schema precisely. Only recommend buying players from the provided transferMarket list.
        Your reasoning should be brief and reflect whether it's a tactical week or a strategic week.`;

        const response = await ai.models.generateContent({
            model,
            contents: prompt,
            config: {
                responseMimeType: 'application/json',
                responseSchema: aiPlayerResponseSchema,
            },
        });
        return JSON.parse(response.text);
    },

    generateCupsetHeadline: async (winnerName: string, loserName: string, winnerLeague: string, loserLeague: string): Promise<string> => {
        const ai = getAiClient();
        if (!ai) {
            throw new Error("AI Service is not available.");
        }
        const prompt = `You are a dramatic sports newspaper headline writer. A huge cup upset just happened.
The winner is ${winnerName} from the ${winnerLeague}.
The loser is ${loserName} from the ${loserLeague}.
Write one short, punchy, and dramatic headline (no more than 10 words) for this shocking result. Examples: "GIANTS SLAYN!", "MINNOWS MAUL MIGHTY MARLINS", "SHOCKWAVES AS REDS DUMPED OUT BY ROVERS".`;

        const response = await ai.models.generateContent({ model, contents: prompt });
        return response.text.replace(/"/g, '');
    },

    generatePunditTake: async (player: Player, team: TeamInLeague): Promise<string> => {
        const ai = getAiClient();
        if (!ai) {
            throw new Error("AI Service is not available. API key might be missing.");
        }

        const isStarter = Object.values(team.tactics.lineup).includes(player.id);
        const tacticalPosition = Object.keys(team.tactics.lineup).find(pos => team.tactics.lineup[pos as any] === player.id);

        const prompt = `
            You are a knowledgeable and slightly dramatic football pundit. Write a short, punchy, analysis of the following player within the context of their team's tactics.

            **Player Profile:**
            - Name: ${player.name}
            - Age: ${player.age}
            - Position: ${player.position}
            - Skill: ${player.skill}
            - Potential: ${player.potential}
            - Form: ${player.form > 0 ? `+${player.form}` : player.form}
            - Personality: ${player.personality}
            - Key Attributes: ${Object.entries(player.attributes).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([key, val]) => `${key} (${Math.round(val)})`).join(', ')}

            **Team Tactical Context:**
            - Team: ${team.name}
            - Formation: ${team.tactics.formation}
            - Mentality: ${team.tactics.mentality}
            - Player's Role: ${isStarter ? `Starter at ${tacticalPosition}` : 'Reserve Player'}

            **Your Task:**
            Based on all this information, provide a punchy, insightful, and entertaining summary (2-3 sentences).
            - What kind of player are they and what are their strengths?
            - How do they fit into the team's current tactical setup?
            - What is their future outlook and potential benefit to the team?
        `;
        
        const response = await ai.models.generateContent({ model, contents: prompt });
        return response.text.replace(/"/g, '');
    },

    generateOpeningDialogue: async (player: Player, team: TeamInLeague): Promise<string> => {
        const ai = getAiClient();
        if (!ai) {
            throw new Error("AI Service is not available. API key might be missing.");
        }

        const prompt = `
            You are writing dialogue for a football management game. A player, ${player.name}, has requested a meeting with the manager.
            Generate a single, short, in-character opening line for the player to say to the manager.

            **Player Context:**
            - Name: ${player.name}
            - Personality: ${player.personality} (e.g., Loyal, Ambitious, Hot-headed, Professional)
            - Morale: ${player.morale}
            - Reason for meeting: "${player.moraleReason}"
            
            Write one sentence from the player's perspective that reflects their personality, morale, and reason for the meeting.
            For example:
            - A 'Hot-headed' player unhappy with game time might say: "Gaffer, I'm wasting away on the bench, what's the deal?"
            - A 'Loyal' player unhappy with their contract might say: "Boss, can we talk? I was hoping we could sort out my future here."
            - An 'Ambitious' player who wants a new challenge might say: "Gaffer, I feel like I'm ready for the next step in my career and wanted to discuss it with you."
        `;
        const response = await ai.models.generateContent({ model, contents: prompt });
        return response.text.replace(/"/g, '');
    },
    
    generateMatchReport: async (gameState: GameState, fixture: DisplayFixture): Promise<{title: string, message: string}> => {
        const ai = getAiClient();
        if (!ai) {
            throw new Error("AI Service is not available. API key might be missing.");
        }

        const { result, home, away, competition } = fixture;
        const allTeams = Object.values(gameState.leagues).flatMap(l=>l.teams);
        const homeTeam = allTeams.find(t => t.name === home);
        const awayTeam = allTeams.find(t => t.name === away);

        if (!result || !homeTeam || !awayTeam) {
            return { title: "Match Report", message: "Match data incomplete." };
        }

        const homeScorersText = result.homeScorers.map((id: number) => getPlayerByIdUnsafe(gameState, id)?.name).join(', ') || 'None';
        const awayScorersText = result.awayScorers.map((id: number) => getPlayerByIdUnsafe(gameState, id)?.name).join(', ') || 'None';
        const potmText = result.potm ? `${getPlayerByIdUnsafe(gameState, result.potm.playerId)?.name} (${result.potm.rating.toFixed(1)})` : 'None';

        const prompt = `
            You are a football journalist writing for a newspaper. Generate a headline and a short match report (2-3 paragraphs) for the following game. Be dramatic and engaging.

            **Match Data:**
            - Match: ${home} vs ${away}
            - Competition: ${competition}
            - Final Score: ${home} ${result.homeScore} - ${result.awayScore} ${away}
            - Key Stat: Possession was ${result.homePossession}% for ${home} and ${result.awayPossession}% for ${away}.
            - Goal Scorers (${home}): ${homeScorersText}
            - Goal Scorers (${away}): ${awayScorersText}
            - Player of the Match: ${potmText}

            **Instructions:**
            1. Write a punchy, attention-grabbing newspaper headline (max 10 words).
            2. Write a 2-3 paragraph match report summarizing the game's narrative. Mention key events, standout performances (especially the POTM), and what this result means for the teams.
            3. Format your response as:
               HEADLINE: [Your headline here]
               REPORT: [Your report here]
        `;

        const response = await ai.models.generateContent({ model, contents: prompt });
        const text = response.text;
        const headlineMatch = text.match(/HEADLINE: (.*)/);
        const reportMatch = text.match(/REPORT: ([\s\S]*)/);
        
        const title = headlineMatch ? headlineMatch[1].trim() : `${home} ${result.homeScore} - ${result.awayScore} ${away}`;
        const message = reportMatch ? reportMatch[1].trim() : text;

        return { title, message };
    },
    
    generateManagerGrade: async (seasonSummary: any): Promise<string> => {
        const ai = getAiClient();
        if (!ai) {
            console.warn("AI service unavailable for manager grade, using fallback.");
            if (seasonSummary.position <= seasonSummary.promotionPlaces) return "A+: Exceeded all expectations with a brilliant promotion-winning campaign.";
            if (seasonSummary.objective.includes('promotion') && seasonSummary.position <= seasonSummary.playoffPlaces) return "B: A strong season, securing a playoff spot as targeted.";
            if (seasonSummary.objective.includes('Avoid relegation') && seasonSummary.position < seasonSummary.leagueSize - seasonSummary.relegationPlaces) return "C: Job done. Survived the drop, which was the main objective.";
            return "C: A solid, if unspectacular, season that met expectations.";
        }

        try {
            const prompt = `
                You are a cynical but fair football pundit. Based on the following end-of-season summary for a manager, provide a single letter grade (A+, A, B+, B, C, D, F) and a short, one-sentence justification.
                
                **Season Summary:**
                - Team: ${seasonSummary.teamName}
                - League: ${seasonSummary.leagueName}
                - Final Position: ${seasonSummary.position} out of ${seasonSummary.leagueSize}
                - Expected Outcome: ${seasonSummary.objective}
                - Record (W-D-L): ${seasonSummary.wins}-${seasonSummary.draws}-${seasonSummary.losses}
                - Cup Performance: ${seasonSummary.cupPerformance}
                - Net Spend: £${(seasonSummary.income - seasonSummary.spent).toLocaleString()}

                Format your response as: GRADE: [Your justification]
                Example: C+: A solid, if unspectacular, season that met expectations without pulling up any trees.
            `;

            const response = await ai.models.generateContent({ model, contents: prompt });
            return response.text.replace(/"/g, '');
        } catch (e) {
            console.error("AI manager grade generation failed, using fallback.", e);
            if (seasonSummary.position <= seasonSummary.promotionPlaces) return "A+: Exceeded all expectations with a brilliant promotion-winning campaign.";
            if (seasonSummary.objective.includes('promotion') && seasonSummary.position <= seasonSummary.playoffPlaces) return "B: A strong season, securing a playoff spot as targeted.";
            if (seasonSummary.objective.includes('Avoid relegation') && seasonSummary.position < seasonSummary.leagueSize - seasonSummary.relegationPlaces) return "C: Job done. Survived the drop, which was the main objective.";
            return "C: A solid, if unspectacular, season that met expectations.";
        }
    },
    
    generateMatchHeadline: async (result: Result, homeTeamName: string, awayTeamName: string): Promise<string> => {
        const ai = getAiClient();
        if (!ai) {
            console.warn("AI service unavailable for match headline, using fallback.");
            const { homeScore, awayScore, isSignificant } = result;
            if (isSignificant && (homeScore > awayScore && homeTeamName < awayTeamName) || (awayScore > homeScore && awayTeamName < homeTeamName)) return "SHOCK UPSET!";
            if (isSignificant && homeScore + awayScore >= 7) return "GOAL FEST!";
            if (homeScore > awayScore) return `${homeTeamName.split(' ').pop()} claim victory!`.toUpperCase();
            if (awayScore > homeScore) return `${awayTeamName.split(' ').pop()} win on the road!`.toUpperCase();
            return "Honours Even".toUpperCase();
        }

        try {
            const prompt = `
                You are a sports newspaper headline writer. Write one short, punchy, and dramatic headline (no more than 10 words) for the following football match result.

                **Match Result:**
                - ${homeTeamName} ${result.homeScore} - ${result.awayScore} ${awayTeamName}
                - Significant because: ${result.isSignificant ? "it was a major upset or a high-scoring thriller" : "it was a standard match"}

                Examples: "BLUES CRUISE TO VICTORY", "REDS STUNNED IN SHOCK DEFEAT", "CLASSIC THRILLER ENDS ALL SQUARE"
            `;
            const response = await ai.models.generateContent({ model, contents: prompt });
            return response.text.replace(/"/g, '');
        } catch (e) {
            console.error("AI match headline generation failed, using fallback.", e);
            const { homeScore, awayScore, isSignificant } = result;
            if (isSignificant && (homeScore > awayScore && homeTeamName < awayTeamName) || (awayScore > homeScore && awayTeamName < homeTeamName)) return "SHOCK UPSET!";
            if (isSignificant && homeScore + awayScore >= 7) return "GOAL FEST!";
            if (homeScore > awayScore) return `${homeTeamName.split(' ').pop()} claim victory!`.toUpperCase();
            if (awayScore > homeScore) return `${awayTeamName.split(' ').pop()} win on the road!`.toUpperCase();
            return "Honours Even".toUpperCase();
        }
    },
    
    generatePlayerRoleAnalysis: async (player: Player, team: TeamInLeague, tacticalPosition: string): Promise<{ roleName: string; roleDescription: string }> => {
        const ai = getAiClient();
        if (!ai) {
            throw new Error("AI Service is not available.");
        }

        const prompt = `
            You are a world-class football analyst. Your task is to analyze a player and assign them a specific tactical role based on their attributes and position in the team's formation.

            **Player Profile:**
            - Name: ${player.name}
            - Age: ${player.age}
            - General Position: ${player.position}
            - Specific Tactical Position in ${team.tactics.formation} formation: ${tacticalPosition}
            - Key Attributes: ${Object.entries(player.attributes).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([key, val]) => `${key} (${Math.round(val)})`).join(', ')}

            **Your Task:**
            1.  Assign a specific, common football role name (e.g., "Ball-Winning Midfielder", "Target Man", "Sweeper Keeper", "Inverted Winger").
            2.  Write a brief (1-2 sentences) description for this role that connects it to the player's key attributes. For example, if they are a "Poacher", mention their high "Finishing" and "Positioning".

            Provide the response in a JSON object that matches the required schema.
        `;
        
        const response = await ai.models.generateContent({
            model,
            contents: prompt,
            config: {
                responseMimeType: 'application/json',
                responseSchema: playerRoleAnalysisSchema,
            },
        });
        return JSON.parse(response.text);
    },

    generatePlayerComparison: async (playerA: Player, playerB: Player, position: string): Promise<string> => {
        const ai = getAiClient();
        if (!ai) { throw new Error("AI Service is not available."); }
    
        const serializeForAI = (p: Player) => ({
            name: p.name,
            age: p.age,
            position: p.position,
            skill: p.skill,
            form: p.form,
            attributes: p.attributes,
        });

        const prompt = `You are an expert football scout. Compare two players for the specific role of ${position}.
        Provide a concise, one-paragraph analysis highlighting their key differences and conclude with a definitive recommendation for who is better for this role right now.
    
        Player 1 (${playerA.name}): ${JSON.stringify(serializeForAI(playerA))}
        Player 2 (${playerB.name}): ${JSON.stringify(serializeForAI(playerB))}
    
        Analyze their attributes and stats to determine the better fit.`;
    
        const response = await ai.models.generateContent({ model, contents: prompt });
        return response.text;
    },

    generateAssistantManagerTake: async (player: Player): Promise<string> => {
        const ai = getAiClient();
        if (!ai) { throw new Error("AI Service is not available."); }
        
        let context = '';
        if (player.fitness < 40) {
            context = `The player is unfit (Fitness: ${player.fitness}%).`;
        } else {
            context = `The player is unhappy (Morale: ${player.morale}). The stated reason is: "${player.moraleReason}".`;
        }

        const prompt = `
            You are a sharp, insightful assistant manager. You're talking directly to the manager (gaffer).
            Player ${player.name} is struggling.
            Context: ${context}
            
            Provide a very short, one-sentence piece of advice or analysis. Be direct and use football slang.
            Examples:
            - "He's running on empty, gaffer. Needs a rest before he breaks down."
            - "His head's dropped after the last few games. Might need an arm around the shoulder."
            - "He's fuming about his contract, boss. We need to sort it or sell him."
        `;

        const response = await ai.models.generateContent({ model, contents: prompt });
        return response.text.replace(/"/g, '');
    },

    generateStoryOfTheSeason: async (seasonSummary: any): Promise<string> => {
        const ai = getAiClient();
        if (!ai) {
            console.warn("AI service unavailable for story of the season.");
            return "A season of determination and grit. Your team battled through challenges and came away with valuable experience for the campaign ahead.";
        }

        try {
            const prompt = `
                You are a sports journalist writing a compelling narrative summary of a football manager's season.
                Write a 2-3 sentence story of the season that captures the drama, achievements, and key moments.
                Make it engaging and memorable - like you're telling the story to a fellow manager over a drink.
                Keep the tone appropriate to the outcome (celebratory for success, philosophical for struggle, proud for mid-table survival, etc).

                **Season Summary:**
                - Team: ${seasonSummary.teamName} (${seasonSummary.leagueName})
                - Final Position: ${seasonSummary.position} out of ${seasonSummary.leagueSize}
                - Record: ${seasonSummary.wins} wins, ${seasonSummary.draws} draws, ${seasonSummary.losses} losses
                - Goals For: ${seasonSummary.goalsFor}, Goals Against: ${seasonSummary.goalsAgainst}
                - Cup Performance: ${seasonSummary.cupPerformance}
                - Key Achievement: ${seasonSummary.achievement}
                - Manager Focus: ${seasonSummary.objective}

                Write only the narrative - no labels, no formatting, just the story.
            `;

            const response = await ai.models.generateContent({ model, contents: prompt });
            return response.text.trim();
        } catch (e) {
            console.error("AI story of the season generation failed, using fallback.", e);
            return "A season of determination and grit. Your team battled through challenges and came away with valuable experience for the campaign ahead.";
        }
    },

    isAiAvailable: (): boolean => {
        getAiClient(); // Ensures the check has run
        return aiInstance !== null;
    },
};